"use client";

import { useQuery } from "@tanstack/react-query";

import type { GoodLuckCoreDTOsTournamentMyRegistrationDto } from "@/shared/api/generated-kubb/types";
import { api } from "@/shared/lib/api";

/**
 * Статус собственной заявки на турнир — ОДИН источник на всю страницу.
 *
 * KAN-935. До этого ручку спрашивали дважды на каждое открытие: блок кнопок
 * (`tournament-actions`) ходил сырым `api.get` вообще мимо React Query, а вкладки
 * (`tournament-detail-tabs`) — своим ключом. Кэш не спасал: разные механизмы —
 * разные запросы, даже с одинаковым URL.
 *
 * Ключ намеренно во множественном префиксе `["tournaments", id, …]`: под ним уже
 * живут остальные подресурсы турнира, и `refreshAfterRegistrationChange` их
 * инвалидирует пачкой.
 */

/** Тип ответа — из Kubb (KAN-1182): бэкенд объявил ProducesResponseType. */
export type MyRegistrationResponse = GoodLuckCoreDTOsTournamentMyRegistrationDto;

export const myRegistrationQueryKey = (tournamentId: string) =>
  ["tournaments", tournamentId, "my-registration"] as const;

export function useMyRegistration(
  tournamentId: string | undefined,
  options?: { enabled?: boolean },
) {
  const id = tournamentId ?? "";
  return useQuery<MyRegistrationResponse | null>({
    queryKey: myRegistrationQueryKey(id),
    queryFn: async () => {
      const { data } = await api.get<MyRegistrationResponse | null>(
        `/tournaments/${id}/my-registration`,
      );
      return data ?? null;
    },
    enabled: Boolean(id) && options?.enabled !== false,
    // Статус меняет сам пользователь (регистрация/отмена) и организатор
    // (approve/reject). Первое инвалидируется явно, второе — редкость, ради
    // которой не стоит держать опрос: страница и так перечитывается по возврату.
    staleTime: 60_000,
    // Глобальный дефолт проекта — refetchOnMount: false, но здесь он опасен:
    // организатор может подтвердить/отклонить заявку, пока человек ходит по
    // другим страницам, и тот вернулся бы к устаревшему статусу из кэша.
    // На дедупликацию это не влияет: одновременный маунт двух потребителей
    // React Query всё равно схлопывает в один запрос.
    refetchOnMount: true,
    retry: false,
  });
}
